import { AppstoreOutlined, CameraOutlined, ClockCircleOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Empty, Spin, Switch } from 'antd';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const ORANGE = '#ff8a24';
const TEXT = '#202124';
const MUTED = '#8b9098';

export interface StoreInspectionPictureDetailTarget {
	messageId?: string;
	picId?: string;
	planId?: string;
	storeId?: string;
	storeName?: string;
	deviceAutoId?: string;
	deviceSn?: string;
	deviceName?: string;
	channelId?: string;
	imageUrl?: string;
	imageUrlRaw?: string;
	imageFallbackUrl?: string;
	createdAt?: string;
	title?: string;
}

interface StoreInspectionPictureDetailProps {
	target: StoreInspectionPictureDetailTarget | null;
	loading?: boolean;
	imageLoading?: boolean;
	showHeader?: boolean;
	showEditMark?: boolean;
	headerBackground?: string;
	onBack?: () => void;
	onLive: () => void;
	onPlayback: () => void;
	onCreateEvent: () => void;
	onDownload: () => void;
	empty?: ReactNode;
}

export function pictureDetailDate(value: unknown): string {
	const timestamp = looseTimestamp(value);
	if (!Number.isFinite(timestamp)) return '';
	const date = new Date(timestamp);
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function pictureDetailTime(value: unknown): string {
	const timestamp = looseTimestamp(value);
	if (!Number.isFinite(timestamp)) return value == null ? '' : String(value);
	const date = new Date(timestamp);
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

export function normalizePictureCaptureTime(value: unknown): string {
	return pictureDetailTime(value);
}

export function buildStoreInspectionPlayerUrl(
	target: StoreInspectionPictureDetailTarget | null | undefined,
	mode: 'live' | 'playback',
): string {
	if (!target?.storeId || !target.deviceSn) return '';
	const params = new URLSearchParams();
	params.set('storeId', target.storeId);
	params.set('storeName', target.storeName || '');
	params.set('deviceSn', target.deviceSn);
	params.set('deviceLabel', target.deviceName || target.deviceSn);
	if (target.deviceAutoId) params.set('deviceAutoId', target.deviceAutoId);
	params.set('channel', target.channelId || '1');
	params.set('mode', mode);
	if (mode === 'playback') {
		const captureTime = normalizePictureCaptureTime(target.createdAt);
		const date = pictureDetailDate(captureTime || target.createdAt);
		if (date) params.set('date', date);
		if (captureTime) params.set('at', captureTime);
	}
	return `/m/player?${params.toString()}`;
}

export function StoreInspectionPictureDetail({
	target,
	loading = false,
	imageLoading = false,
	showHeader = true,
	showEditMark = false,
	headerBackground = '#fff',
	onBack,
	onLive,
	onPlayback,
	onCreateEvent,
	onDownload,
	empty,
}: StoreInspectionPictureDetailProps) {
	const { t } = useTranslation();
	const [intelligentOpen, setIntelligentOpen] = useState(false);
	const [intelligentEnabled, setIntelligentEnabled] = useState(false);
	const [activeImageUrl, setActiveImageUrl] = useState(target?.imageUrl ?? '');
	const title = target?.title || [target?.storeName, target?.deviceName || target?.deviceSn].filter(Boolean).join('/') || t('native.home.store_inspection_message', { defaultValue: 'Store Inspection Message' });
	const createdAt = pictureDetailTime(target?.createdAt);

	useEffect(() => {
		setActiveImageUrl(target?.imageUrl ?? '');
	}, [target?.imageUrl]);

	const handleImageError = () => {
		if (target?.imageFallbackUrl && activeImageUrl !== target.imageFallbackUrl) {
			setActiveImageUrl(target.imageFallbackUrl);
			return;
		}
		setActiveImageUrl('');
	};

	return (
		<div style={detailRootStyle}>
			{showHeader ? (
				<div style={{ ...detailHeaderStyle, background: headerBackground }}>
					<button type="button" aria-label={t('common.back')} onClick={onBack} style={backButtonStyle}>
						<span aria-hidden="true" style={backIconStyle} />
					</button>
					<div style={headerTextStyle}>
						<div style={headerTitleStyle}>{title}</div>
						{createdAt ? <div style={headerTimeStyle}>{createdAt}</div> : null}
					</div>
					<span />
				</div>
			) : (
				createdAt ? <div style={subtitleStyle}>{createdAt}</div> : null
			)}
			{loading ? (
				<div style={centerStyle}><Spin /></div>
			) : target ? (
				<>
					<div style={heroStyle}>
						{activeImageUrl ? (
							<img src={activeImageUrl} alt={title} style={heroImageStyle} onError={handleImageError} />
						) : (
							<div style={noImageStyle}>
								{imageLoading ? <Spin /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('native.common.no_data', { defaultValue: 'No Data' })} />}
							</div>
						)}
						{showEditMark ? (
							<span style={editMarkStyle} aria-hidden="true">
								<EditOutlined />
							</span>
						) : null}
						<div style={overlayStyle}>
							<button type="button" style={overlayButtonStyle} onClick={onLive}>
								<CameraOutlined />
								<span>{t('player.live', { defaultValue: 'Live' })}</span>
							</button>
							<button type="button" style={overlayButtonStyle} onClick={() => setIntelligentOpen(true)}>
								<AppstoreOutlined />
								<span>{t('native.inspect.intelligent_options')}</span>
							</button>
							<button type="button" style={overlayButtonStyle} onClick={onPlayback}>
								<ClockCircleOutlined />
								<span>{t('player.playback', { defaultValue: 'Playback' })}</span>
							</button>
						</div>
					</div>
					<div style={spacerStyle} />
				</>
			) : (
				empty ?? <div style={centerStyle}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('native.common.no_data', { defaultValue: 'No Data' })} /></div>
			)}
			<div style={bottomBarStyle}>
				<div style={bottomInnerStyle}>
					<button type="button" style={bottomButtonStyle} onClick={onCreateEvent}>
						{t('native.inspect.create_event')}
					</button>
					<button type="button" style={bottomButtonStyle} onClick={onDownload}>
						{t('native.inspect.download_picture')}
					</button>
				</div>
			</div>
			{intelligentOpen ? (
				<div style={intelligentBackdropStyle}>
					<div style={intelligentDialogStyle}>
						<button type="button" aria-label={t('common.close', { defaultValue: 'Close' })} onClick={() => setIntelligentOpen(false)} style={intelligentCloseStyle}>
							<CloseOutlined />
						</button>
						<div style={intelligentTitleStyle}>
							{t('native.inspect.intelligent_options_title')}
						</div>
						<div style={intelligentRowStyle}>
							<span>{t('native.inspect.intelligent_option_roughly')}</span>
							<Switch
								checked={intelligentEnabled}
								onChange={setIntelligentEnabled}
								style={{ background: intelligentEnabled ? ORANGE : 'rgba(255,255,255,0.65)' }}
							/>
						</div>
						<div style={intelligentLearnStyle}>
							{t('native.inspect.intelligent_options_learning')}
							<div style={intelligentTipStyle}>
								{t('native.inspect.intelligent_options_tip')}
							</div>
						</div>
						<button type="button" style={chooseLaterStyle} onClick={() => setIntelligentOpen(false)}>
							{t('native.inspect.choose_later')}
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}

function looseTimestamp(value: unknown): number {
	const text = value == null ? '' : String(value).trim();
	if (!text) return NaN;
	if (/^\d+$/.test(text)) {
		const parsed = Number(text);
		return parsed < 100000000000 ? parsed * 1000 : parsed;
	}
	return new Date(text.replace(/-/g, '/')).getTime();
}

function pad2(value: number): string {
	return String(value).padStart(2, '0');
}

const detailRootStyle: CSSProperties = {
	minHeight: 'calc(100vh - 48px)',
	background: '#f3f3f3',
	paddingBottom: 74,
};

const detailHeaderStyle: CSSProperties = {
	position: 'relative',
	display: 'grid',
	gridTemplateColumns: '56px minmax(0, 1fr) 56px',
	alignItems: 'center',
	minHeight: 82,
	borderBottom: '1px solid #eeeeee',
};

const backButtonStyle: CSSProperties = {
	border: 0,
	background: 'transparent',
	height: 64,
	display: 'grid',
	placeItems: 'center',
};

const backIconStyle: CSSProperties = {
	width: 17,
	height: 17,
	borderLeft: `2px solid ${ORANGE}`,
	borderBottom: `2px solid ${ORANGE}`,
	transform: 'rotate(45deg)',
};

const headerTextStyle: CSSProperties = {
	minWidth: 0,
	textAlign: 'center',
	color: TEXT,
};

const headerTitleStyle: CSSProperties = {
	fontSize: 20,
	fontWeight: 600,
	lineHeight: 1.3,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const headerTimeStyle: CSSProperties = {
	marginTop: 4,
	fontSize: 14,
	color: MUTED,
};

const subtitleStyle: CSSProperties = {
	textAlign: 'center',
	padding: '14px 16px 16px',
	background: '#fff',
	color: MUTED,
	fontSize: 16,
};

const centerStyle: CSSProperties = {
	minHeight: 320,
	display: 'grid',
	placeItems: 'center',
};

const heroStyle: CSSProperties = {
	position: 'relative',
	width: '100%',
	minHeight: 220,
	marginTop: 180,
	background: '#d8d8d8',
	overflow: 'hidden',
};

const heroImageStyle: CSSProperties = {
	width: '100%',
	display: 'block',
	objectFit: 'cover',
};

const noImageStyle: CSSProperties = {
	minHeight: 220,
	display: 'grid',
	placeItems: 'center',
	background: '#eeeeee',
};

const editMarkStyle: CSSProperties = {
	position: 'absolute',
	right: 20,
	bottom: 56,
	width: 34,
	height: 34,
	borderRadius: 17,
	border: '2px solid rgba(255,255,255,0.9)',
	color: '#fff',
	display: 'grid',
	placeItems: 'center',
	background: 'rgba(0,0,0,0.25)',
	fontSize: 18,
};

const overlayStyle: CSSProperties = {
	position: 'absolute',
	left: 0,
	right: 0,
	bottom: 0,
	display: 'grid',
	gridTemplateColumns: '1fr 1.5fr 1fr',
	alignItems: 'center',
	minHeight: 48,
	background: 'rgba(0,0,0,0.55)',
};

const overlayButtonStyle: CSSProperties = {
	minWidth: 0,
	border: 0,
	background: 'transparent',
	color: '#fff',
	fontSize: 17,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 8,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
};

const spacerStyle: CSSProperties = {
	minHeight: '34vh',
	background: '#f3f3f3',
};

const bottomBarStyle: CSSProperties = {
	position: 'fixed',
	left: 0,
	right: 0,
	bottom: 0,
	background: '#fff',
	borderTop: '1px solid #eeeeee',
	zIndex: 20,
};

const bottomInnerStyle: CSSProperties = {
	maxWidth: 720,
	margin: '0 auto',
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
};

const bottomButtonStyle: CSSProperties = {
	height: 64,
	border: 0,
	borderRight: '1px solid #eeeeee',
	background: '#fff',
	color: ORANGE,
	fontSize: 17,
};

const intelligentBackdropStyle: CSSProperties = {
	position: 'fixed',
	inset: 0,
	zIndex: 1000,
	background: 'rgba(0,0,0,0.65)',
	display: 'grid',
	placeItems: 'center',
	padding: '0 38px',
};

const intelligentDialogStyle: CSSProperties = {
	position: 'relative',
	width: '100%',
	maxWidth: 620,
	borderRadius: 6,
	overflow: 'hidden',
	background: 'rgba(90, 160, 154, 0.9)',
	color: '#fff',
	textAlign: 'center',
};

const intelligentCloseStyle: CSSProperties = {
	position: 'absolute',
	right: 14,
	top: 14,
	border: 0,
	background: 'transparent',
	color: 'rgba(255,255,255,0.9)',
	fontSize: 20,
};

const intelligentTitleStyle: CSSProperties = {
	padding: '42px 16px 32px',
	fontSize: 22,
	lineHeight: 1.35,
	borderBottom: '1px solid rgba(255,255,255,0.9)',
};

const intelligentRowStyle: CSSProperties = {
	minHeight: 70,
	padding: '0 36px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 18,
	borderBottom: '1px solid rgba(255,255,255,0.35)',
	fontSize: 17,
	textAlign: 'left',
};

const intelligentLearnStyle: CSSProperties = {
	padding: '26px 32px 28px',
	fontSize: 17,
	lineHeight: 1.35,
	borderBottom: '1px solid rgba(255,255,255,0.9)',
};

const intelligentTipStyle: CSSProperties = {
	marginTop: 20,
	fontSize: 14,
	lineHeight: 1.55,
	textAlign: 'left',
	color: 'rgba(255,255,255,0.8)',
};

const chooseLaterStyle: CSSProperties = {
	width: '100%',
	height: 68,
	border: 0,
	background: 'rgba(255,255,255,0.05)',
	color: '#fff',
	fontSize: 20,
};
